import { LitElement, PropertyValues, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { BubbleModalClickEvent, BubbleModalPositionChangeEvent } from '../BubbleModal/events';
import styles from './FloatingBubble.scss?inline';
import { FloatingBubbleStorage } from './utils/storage';


const CLICK_SENSITIVITY = 5; // distancia máxima para considerar un click vs un drag
const MARGIN = 10; // margen para el snapping al borde

@customElement('app-floating-bubble')
export class FloatingBubbleComponent extends LitElement {
    @property({ type: Number, reflect: true }) k_zIndexBase: number = 100;
    @property({ type: Number, reflect: true }) k_size: number = 60;
    @property({ type: String, reflect: false }) k_backgroundColor: string | null = null;
    @property({ type: Boolean, reflect: true }) k_draggable: boolean = true;
    @property({ type: Number, reflect: true }) k_horizontalMargin: number = MARGIN;
    @property({ type: Number, reflect: true }) k_verticalMargin: number = MARGIN;
    @property({ type: Number, reflect: true }) k_marginTop: number | null = null;
    @property({ type: Number, reflect: true }) k_marginBottom: number | null = null;
    @property({ type: Number, reflect: true }) k_marginLeft: number | null = null;
    @property({ type: Number, reflect: true }) k_marginRight: number | null = null;
    @property({ type: String, reflect: true }) k_storageKey: string | number = null;

    @state() private _x = 0;
    @state() private _y = 0;
    @state() private _scale = 0;
    @state() private _isDragging = false;
    @state() private _isEnterAnimationActive = false;
    // @state() private _isSnapping = false;

    @query('.bubble-inner') private _bubbleInner: HTMLElement;

    public k_parentBounds: HTMLElement | null = null;

    private startX = 0;
    private startY = 0;
    private pointerDownEvent: PointerEvent | null = null;
    private storage: FloatingBubbleStorage = new FloatingBubbleStorage();

    static styles = [unsafeCSS(styles)];

    /** Auxiliar para detectar click con un area segura */
    private startClickPosition: Vector | null = null;

    render() {
        return html`
    <div class="bubble-inner"
            style=${this.getStyleString()}
            @pointerdown=${this.onPointerDown}>
        <slot></slot>
    </div>
    `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.forceEnterAnimation();

        this.setAttribute('tabindex', '0'); // hacer focusable para accesibilidad

        window.addEventListener('resize', () => this.adjustBubblePosition());

        setTimeout(() => this.adjustBubblePosition());
    }

    protected updated(_changedProperties: PropertyValues): void {
        super.updated(_changedProperties);

        if (_changedProperties.has('k_size') && !this._isDragging) {
            this.adjustBubblePosition();
        }
        if (_changedProperties.has('k_storageKey')) {
            this.storage._StorageKey = this.k_storageKey;
            const lastPosition = this.storage._LastPosition;
            if (lastPosition) {
                this._SetPosition(lastPosition);
            }
        }
        if (!this._isDragging && this.k_storageKey && (_changedProperties.has('_x') || _changedProperties.has('_y'))) {
            this.storage._SetStoragePosition({ x: this._x, y: this._y });
        }
    }

    // **********************************************************************
    // #region Utils

    private getStyles() {
        const useTransitionCSS: boolean = !this._isDragging && !this._isEnterAnimationActive;
        const useTransformCSS: boolean = !this._isEnterAnimationActive;
        const styles: { [key: string]: string } = {
            "z-index": this.k_zIndexBase.toString(),
            "--size": `${this.k_size}px`,
            "--backgroundColor": this.k_backgroundColor || null,
            "--x": `${this._x}px`,
            "--y": `${this._y}px`,
            "--scale": this._isDragging ? '1.4' : this._scale.toString(),
            "--transition": useTransitionCSS ? 'transform 0.1s ease-out, width 0.1s ease-out, height 0.1s ease-out' : null,
            "--transform": useTransformCSS ? `translate3d(var(--x), var(--y), 0) scale(var(--scale))` : null
        };
        return styles;
    }

    private getStyleString() {
        const styles = this.getStyles();
        return Object.entries(styles)
            .filter(([_, value]) => value !== null)
            .map(([key, value]) => `${key}: ${value};`).join(' ');
    }

    /** Implementa la siguiente animación de entrada usando API de Web Animations:
     * animation: pop-in 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both
     * */
    private forceEnterAnimation(durationMs: number = 500) {
        this._isEnterAnimationActive = true;
        // this.scale = 0;
        const varX = `var(--x)`;
        const varY = `var(--y)`;
        const finalScale = this._scale === 0 ? 1 : this._scale;
        const animation = this._bubbleInner.animate([
            {
                opacity: 0,
                transform: `translate3d(${varX}, ${varY}, 0) scale(${finalScale / 2})`
            },
            {
                transform: `translate3d(${varX}, ${varY}, 0) scale(${finalScale * 1.5})`
            },
            {
                opacity: 1,
                transform: `translate3d(${varX}, ${varY}, 0) scale(${finalScale})`
            }
        ], {
            duration: durationMs,
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            fill: 'both',
        });

        animation.onfinish = () => {
            if (this._scale === 0) {
                this._scale = 1;
            }
            this._isEnterAnimationActive = false;

            animation.cancel(); // remover animación para evitar conflictos con transformaciones css y posteriores
        };
    }

    private getParentReference() {
        return this.k_parentBounds || this.parentElement || document.body;
    }

    private calculatePositionWithEvent(e: PointerEvent): Vector {
        const parent = this.getParentReference();
        const bounds = parent.getBoundingClientRect();

        let newX = e.clientX - this.startX;
        let newY = e.clientY - this.startY;

        // aplica limites (boundaries)
        newX = Math.max(0, Math.min(newX, bounds.width - this.k_size));
        newY = Math.max(0, Math.min(newY, bounds.height - this.k_size));

        return { x: newX, y: newY };
    }

    private isPointInsideBubble(point: Vector): boolean {
        const bubbleRect = {
            left: this._x,
            top: this._y,
            right: this._x + this.k_size,
            bottom: this._y + this.k_size
        }
        return (
            point.x >= bubbleRect.left &&
            point.x <= bubbleRect.right &&
            point.y >= bubbleRect.top &&
            point.y <= bubbleRect.bottom
        );
    }

    // #endregion
    // **********************************************************************

    // **********************************************************************
    // #region API

    public _SetPosition({ x, y }: Vector) {
        this._x = x;
        this._y = y;
        this.adjustBubblePosition();
    }

    public _GetPosition(): Vector {
        return { x: this._x, y: this._y };
    }

    public _SetScale(scale: number) {
        this._scale = scale;
    }

    public _OnClick(callback: (e: Event) => void) {
        const lastListener = this["__lastClickListener"];
        if (lastListener) {
            this.removeEventListener('bubble-click', lastListener);
        }
        this["__lastClickListener"] = callback;
        if (callback) {
            this.addEventListener('bubble-click', callback);
        }
    }

    // #endregion
    // **********************************************************************

    // **********************************************************************
    // #region EVENTS

    private onClick() {
        if (!this.startClickPosition) return;

        const distance = Math.hypot(this._x - this.startClickPosition.x, this._y - this.startClickPosition.y);
        if (distance < CLICK_SENSITIVITY) {
            this.dispatchEvent(new CustomEvent('bubblemodal-click', { bubbles: true, composed: true }) satisfies BubbleModalClickEvent);
        }
    }

    private onPositionChange() {
        this.dispatchEvent(new CustomEvent('bubblemodal-position-change', {
            detail: { x: this._x, y: this._y },
            bubbles: true,
            composed: true
        }) satisfies BubbleModalPositionChangeEvent);
    }

    // #endregion
    // **********************************************************************

    // **********************************************************************
    // #region Drag & Drop Logic

    private onPointerDown(e: PointerEvent) {
        this.startClickPosition = { x: this._x, y: this._y };
        this.pointerDownEvent = e;

        if (!this.k_draggable) {
            window.addEventListener('pointermove', this.onPointerMove);
            window.addEventListener('pointerup', this.onPointerUp);
            return;
        }

        this.activeDrag();
    }

    private onPointerMove = (e: PointerEvent) => {
        if (!this._isDragging) {
            if (!this.k_draggable) return;
            this.activeDrag();
        }

        const newPosition = this.calculatePositionWithEvent(e);
        this._x = newPosition.x;
        this._y = newPosition.y;
    };

    private onPointerUp = (_: PointerEvent) => {
        const pointFinal = { x: this._x, y: this._y };
        if (!this.k_draggable) {
            const isPointInside = this.isPointInsideBubble(pointFinal);
            window.removeEventListener('pointermove', this.onPointerMove);
            window.removeEventListener('pointerup', this.onPointerUp);
            if (isPointInside) {
                this.onClick();
            }
            this.startClickPosition = null;
            return;
        }
        this._isDragging = false;
        this.removeAttribute('active');
        this.classList.add('snapping');

        this.adjustBubblePosition();
        this.onPositionChange();

        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);

        // limpia clase de animación después de que termine
        setTimeout(() => this.classList.remove('snapping'), 400);
        const isPointInside = this.isPointInsideBubble(pointFinal); // obtener después de adjustBubblePosition
        if (isPointInside) {
            this.onClick();
        }
        this.startClickPosition = null;
    };

    private adjustBubblePosition() {
        const parent = this.getParentReference();
        const midPoint = parent.clientWidth / 2;
        const marginLeft = (this.k_marginLeft ?? this.k_horizontalMargin) || 0;
        const marginRight = (this.k_marginRight ?? this.k_horizontalMargin) || 0;
        const marginTop = (this.k_marginTop ?? this.k_verticalMargin) || 0;
        const marginBottom = (this.k_marginBottom ?? this.k_verticalMargin) || 0;

        // ajuste horizontal para hacer snap al borde mas cercano
        if (this._x + this.k_size / 2 < midPoint) {
            this._x = marginLeft;
        } else {
            this._x = parent.clientWidth - this.k_size - marginRight;
        }

        // ajuste vertical para mantener dentro del contenedor
        const maxY = parent.clientHeight - this.k_size - marginBottom;
        if (this._y < marginTop) {
            this._y = marginTop;
        } else if (this._y > maxY) {
            this._y = maxY;
        }
    }

    private activeDrag() {
        const e = this.pointerDownEvent;
        if (!e) return;

        this._isDragging = true;
        // this.isSnapping = false;
        this.setAttribute('active', '');

        // guardar posición inicial relativa al origen de la burbuja
        this.startX = e.clientX - this._x;
        this.startY = e.clientY - this._y;

        // capturar el puntero para seguir moviendo fuera del elemento
        (e.currentTarget as HTMLElement)?.setPointerCapture(e.pointerId);

        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);

        // elevar mientras se arrastra
        this.style.zIndex = (this.k_zIndexBase + 1000).toString();
    }

    // #endregion
    // **********************************************************************
}

export interface Vector {
    x: number;
    y: number;
}